import React from 'react';

/**
 * Card — section shell used across detail pages.
 *
 * Renders the Origin `.card` recipe from components.css. Header gets an
 * optional icon + title and a right-side action slot (commonly an edit
 * pencil or overflow menu).
 *
 * Props:
 *   title   — string title text
 *   icon    — optional lucide icon component (rendered before title)
 *   action  — optional React node placed at the right of the header
 *   compact — use compact header padding
 *   flush   — remove body padding (caller provides its own)
 *   style   — forwarded to the outer .card element
 */
export default function Card({ title, icon: Icon, children, action, compact = false, flush = false, className = '', style }) {
  // The header is what supplies the body's top air — .card-body's padding is
  // `0 24px 20px` precisely because .card-header sits above it. With neither a
  // title nor an action there is no header, and the body would render flush
  // against the card's top edge with 20px hanging below it (doc-303). Flag that
  // case so the stylesheet can restore the symmetry.
  const headerless = !title && !action && !flush;
  return (
    <div className={`card ${className}`} style={style}>
      {(title || action) && (
        <div className={`card-header ${compact ? 'compact' : ''}`}>
          <div className="row" style={{ gap: 8, minWidth: 0, flex: 1 }}>
            {Icon && <Icon className="w-4 h-4" style={{ color: 'var(--icon-secondary)', flexShrink: 0 }} />}
            <span className="card-title">{title}</span>
          </div>
          {action && <div className="row" style={{ gap: 6, flexShrink: 0 }}>{action}</div>}
        </div>
      )}
      <div className={['card-body', flush && 'flush', headerless && 'headerless'].filter(Boolean).join(' ')}>
        {children}
      </div>
    </div>
  );
}
